<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue';
import { useMixins } from '@/plugins/mixins';
import { isImage } from "@/helpers/Utils";
import { DocumentPreviewHeight } from '@/components/forms/blueprints/logito-document-preview';

const props = defineProps({
  "src": null,
  "heightMode": null,
  "height": null
});

const { $filters } = useMixins();
const embedRef = ref<HTMLElement | null>(null);
const parentSectionComponent = ref<HTMLElement | null>(null);
const componentHeight = ref<number | null>(null);

const fileUrl = computed(() =>
{
    const fileUrl = $filters.baseurl(props.src);
    const canRead = /\.(gif|jpe?g|tiff?|png|webp|bmp|svg|pdf)$/i.test(fileUrl);

    return canRead && fileUrl ? fileUrl : null;
});

const isImg = computed(() => isImage(fileUrl.value));

function findParentWithClass(element: HTMLElement | null, className: string): HTMLElement | null
{
    if (!element) return null;

    if (element.classList.contains(className))
    {
        return element;
    }

    return element.parentElement ? findParentWithClass(element.parentElement, className) : null;
}

function calculateComponentHeight()
{
    if (parentSectionComponent.value)
    {
        const rect = parentSectionComponent.value.getBoundingClientRect();
        const topPosition = rect.top;
        const windowHeight = window.innerHeight;
        const paddingAdjustment = 48;

        componentHeight.value = windowHeight - topPosition - paddingAdjustment;

        return componentHeight.value;
    }

    return null;
}

function handleResize()
{
    calculateComponentHeight();
}

function getClasses(): string
{
    const classes: string[] = [];

    if (isImg.value)
    {
        classes.push('image-viewer');
    }
    else
    {
        classes.push('pdf-viewer');
    }

    return classes.join(' ');
}

onMounted(() =>
{
    if (embedRef.value)
    {
        parentSectionComponent.value = findParentWithClass(embedRef.value, 'section-component');

        if (parentSectionComponent.value)
        {
            calculateComponentHeight();
            window.addEventListener('resize', handleResize);
        }
    }
});
</script>

<template>
    <embed
        v-if="fileUrl"
        ref="embedRef"
        :src="fileUrl"
        :class="getClasses()"
        width="100%"
        :height="componentHeight ? `${componentHeight}px` : undefined"
    />
</template>

<style scoped>
.pdf-viewer {
    object-fit: contain;
    position: sticky;
}
</style>
